import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  applyTextOverlay,
  clonePortrait,
  generatePortrait,
  newSessionToken,
  pollStatus,
  type OverlayMode,
} from '../services/api';
import type { InputSetCode } from '../types';
import { recordJob, updateJob } from '../services/jobMonitor';

export type FlowStatus = 'idle' | 'uploading' | 'generating' | 'done' | 'failed';

export interface FlowResult {
  jobId: string;
  previewUrl: string;
  finalUrl: string;
}

export interface FlowState {
  status: FlowStatus;
  jobId: string | null;
  result: FlowResult | null;
  error: string | null;
  sessionToken: string | null;
}

export interface StartParams {
  files: File[];
  productId: string;
  styleId: string;
  portraitMode: string;
  manaquinUrl?: string;
  charCount: number;
  /** Drives the text-overlay endpoint (humanpet / pet / *-angle). */
  inputSetCode: InputSetCode;
  humanName?: string;
  petName?: string;
  petYears?: string;
  petQuote?: string;
  /** Caption modes: single "Family name / message" line. */
  caption?: string;
  /** Caption-overlay text color (hex, from ui_config.caption_color). Omit for default white. */
  captionColor?: string;
  /** familypet-caption: chosen clothing-preference id (keep_original | artist_choice). */
  clothing?: string;
  /** Display name for the admin Job Monitor (?apb_debug=1). */
  styleName?: string;
}

/** Text-overlay context captured at start(), applied after the job completes. */
interface OverlayCtx {
  mode: OverlayMode;
  fields: Pick<StartParams, 'humanName' | 'petName' | 'petYears' | 'petQuote' | 'caption' | 'captionColor'>;
}

/** Map an input_set_code to its overlay endpoint mode. `default` → no overlay. */
function overlayModeFor(code: InputSetCode): OverlayMode | null {
  if (
    code === 'humanpet' ||
    code === 'humanpet-angle' ||
    code === 'pet' ||
    code === 'pet-angle' ||
    code === 'humanpet-caption' ||
    code === 'smallfamilypet-caption' ||
    code === 'familypet-caption'
  ) {
    return code;
  }
  // familypet-caption-45/-50 (SP7-10) reuse the exact same caption-burn
  // overlay/endpoint as familypet-caption — GenerateHandler.php's
  // buildOverlayFromPost() already normalizes their job.overlay.mode to
  // 'familypet-caption', so /familypet-caption-overlay is the right call.
  if (code === 'familypet-caption-45' || code === 'familypet-caption-50') {
    return 'familypet-caption';
  }
  return null;
}

export interface CloneStartParams {
  sourceJobId: string;
  styleId: string;
  portraitMode: string;
  /** Display name for the admin Job Monitor (?apb_debug=1). */
  styleName?: string;
  /** familypet-caption family: chosen clothing-preference id for the retry. */
  clothing?: string;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 180_000; // generation hard timeout — 3 min

const INITIAL: FlowState = {
  status: 'idle',
  jobId: null,
  result: null,
  error: null,
  sessionToken: null,
};

/**
 * useGenerateFlow — orchestrates upload → poll → done | failed (P2-7).
 *
 * One submission per session-token: calling start() while already in flight
 * is a no-op. Call reset() to start over (e.g. for retry with a new style —
 * P3-2 will wire that up).
 */
export function useGenerateFlow() {
  const [state, setState] = useState<FlowState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollStartedAt = useRef<number>(0);
  const overlayCtxRef = useRef<OverlayCtx | null>(null);

  const cleanup = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cancel any in-flight request when the component unmounts.
  useEffect(() => cleanup, [cleanup]);

  const reset = useCallback(() => {
    cleanup();
    setState(INITIAL);
  }, [cleanup]);

  const start = useCallback(
    async (params: StartParams) => {
      if (state.status === 'uploading' || state.status === 'generating') return;

      cleanup();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const sessionToken = newSessionToken();

      // Capture the overlay context for after the job completes.
      const mode = overlayModeFor(params.inputSetCode);
      overlayCtxRef.current = mode
        ? {
            mode,
            fields: {
              humanName: params.humanName,
              petName: params.petName,
              petYears: params.petYears,
              petQuote: params.petQuote,
              caption: params.caption,
              captionColor: params.captionColor,
            },
          }
        : null;

      setState({
        status: 'uploading',
        jobId: null,
        result: null,
        error: null,
        sessionToken,
      });

      let jobId: string;
      try {
        const resp = await generatePortrait(
          { ...params, sessionToken },
          ctrl.signal,
        );
        jobId = resp.job_id;
        // Admin Job Monitor (?apb_debug=1) — log the job once we have its id.
        recordJob({
          job_id: jobId,
          status: 'processing',
          portrait_mode: params.portraitMode,
          style_id: params.styleId,
          style_name: params.styleName ?? params.styleId,
          product_id: params.productId,
          started_at: new Date().toISOString(),
        });
        if (resp.status === 'complete') {
          // Some bridges may return complete synchronously — handle the edge.
          updateJob(jobId, { status: 'completed', completed_at: new Date().toISOString() });
          setState((s) => ({ ...s, status: 'done', jobId }));
          return;
        }
      } catch (err) {
        const msg = err instanceof ApiError ? friendlyError(err) : 'Could not start generation. Please try again.';
        setState((s) => ({ ...s, status: 'failed', error: msg }));
        return;
      }

      setState((s) => ({ ...s, status: 'generating', jobId }));
      pollStartedAt.current = performance.now();
      runPoll(jobId, ctrl.signal);
    },
    // runPoll is defined below and references setState/cleanup via closure;
    // it's stable for our purposes because we recreate it on each start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.status, cleanup],
  );

  const runPoll = useCallback(
    (jobId: string, signal: AbortSignal) => {
      const scheduleNext = () => {
        pollTimerRef.current = window.setTimeout(() => tick(), POLL_INTERVAL_MS);
      };

      const tick = async () => {
        if (signal.aborted) return;
        if (performance.now() - pollStartedAt.current > MAX_POLL_MS) {
          const msg = 'Generation took too long. Please try again.';
          updateJob(jobId, { status: 'failed', error: msg, completed_at: new Date().toISOString() });
          setState((s) => ({ ...s, status: 'failed', error: msg }));
          return;
        }
        try {
          const resp = await pollStatus(jobId, signal);
          if (signal.aborted) return;

          if (resp.status === 'complete' && resp.preview_url && resp.final_url) {
            const finalUrl = resp.final_url;
            // Best-effort text overlay — never blocks the preview. On any
            // error / { skipped: true } / missing url, keep the original image.
            let previewUrl = resp.preview_url;
            const ctx = overlayCtxRef.current;
            if (ctx) {
              try {
                const ov = await applyTextOverlay(
                  ctx.mode,
                  {
                    job_id: jobId,
                    human_name: ctx.fields.humanName || undefined,
                    pet_name: ctx.fields.petName || undefined,
                    pet_years: ctx.fields.petYears || undefined,
                    pet_quote: ctx.fields.petQuote || undefined,
                    caption: ctx.fields.caption || undefined,
                    caption_color: ctx.fields.captionColor || undefined,
                  },
                  signal,
                );
                if (ov && typeof ov.url === 'string' && ov.url) previewUrl = ov.url;
              } catch (err) {
                console.warn('[bf-widget] text overlay failed (non-fatal)', err);
              }
            }
            if (signal.aborted) return;
            updateJob(jobId, { status: 'completed', completed_at: new Date().toISOString(), preview_url: previewUrl });
            setState((s) => ({
              ...s,
              status: 'done',
              result: { jobId, previewUrl, finalUrl },
            }));
            return;
          }
          if (resp.status === 'failed') {
            const msg = resp.error || 'Generation failed. Please try again.';
            updateJob(jobId, { status: 'failed', error: msg, completed_at: new Date().toISOString() });
            setState((s) => ({ ...s, status: 'failed', error: msg }));
            return;
          }
          scheduleNext();
        } catch (err) {
          if (signal.aborted) return;
          // Transient status errors don't kill the flow — keep polling unless
          // it's a real 4xx telling us the job is gone.
          if (err instanceof ApiError && err.code === 'http' && err.status >= 400 && err.status < 500) {
            const msg = friendlyError(err);
            updateJob(jobId, { status: 'failed', error: msg, completed_at: new Date().toISOString() });
            setState((s) => ({ ...s, status: 'failed', error: msg }));
            return;
          }
          scheduleNext();
        }
      };

      // First tick fires after one interval so the worker has time to spin up.
      scheduleNext();
    },
    [],
  );

  const clone = useCallback(
    async (params: CloneStartParams) => {
      if (state.status === 'uploading' || state.status === 'generating') return;

      cleanup();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      // Retry has no text-entry step (preview page) — skip overlay; the bridge
      // already has the names from the original job if it re-applies them.
      overlayCtxRef.current = null;
      // Reuse the existing session token across retries — same buyer, same flow.
      const sessionToken = state.sessionToken ?? newSessionToken();

      setState({
        status: 'uploading',
        jobId: null,
        result: null,
        error: null,
        sessionToken,
      });

      let jobId: string;
      try {
        const resp = await clonePortrait(params, ctrl.signal);
        jobId = resp.job_id;
        // Admin Job Monitor (?apb_debug=1) — log the retry as its own job.
        recordJob({
          job_id: jobId,
          status: 'processing',
          portrait_mode: params.portraitMode,
          style_id: params.styleId,
          style_name: params.styleName ?? params.styleId,
          product_id: '',
          started_at: new Date().toISOString(),
        });
        if (resp.status === 'complete') {
          updateJob(jobId, { status: 'completed', completed_at: new Date().toISOString() });
          setState((s) => ({ ...s, status: 'done', jobId }));
          return;
        }
      } catch (err) {
        const msg = err instanceof ApiError ? friendlyError(err) : 'Could not retry. Please try again.';
        setState((s) => ({ ...s, status: 'failed', error: msg }));
        return;
      }

      setState((s) => ({ ...s, status: 'generating', jobId }));
      pollStartedAt.current = performance.now();
      runPoll(jobId, ctrl.signal);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.status, state.sessionToken, cleanup],
  );

  return { state, start, clone, reset } as const;
}

function friendlyError(err: ApiError): string {
  if (err.code === 'timeout') return 'The request timed out. Please try again.';
  if (err.code === 'network') return 'Network error. Check your connection and try again.';
  if (err.status === 413) return 'Your images are too large. Please use smaller files.';
  // Surface the server's rate-limit notice verbatim (e.g. "You have reached the
  // daily limit of N generations. Limit resets in H hour(s).").
  if (err.status === 429) {
    return err.message && !err.message.startsWith('HTTP ')
      ? err.message
      : 'Daily limit reached. Please try again later.';
  }
  if (err.status >= 500) return 'Our painters are taking a break. Please try again in a moment.';
  return err.message;
}
